import LoadingIcon from "components/Loading/LoadingIcon";
import { useDrop } from "react-dnd";
import type { TaskDataType } from "types/DataTypes";

import KaboodleSwimlaneItem from "./KaboodleSwimlaneItem";
import type { TaskStatus } from "../../utils/taskUtils";

type ColumnProps = {
  title: string;
  items: TaskDataType[];
  columnId: TaskStatus;
  moveItem: (
    itemId: number,
    sourceColumn: TaskStatus,
    targetColumn: TaskStatus,
    targetIndex: number,
  ) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => Promise<void>;
  isRefetching?: boolean;
};

export function KaboodleSwimLaneColumn({
  title,
  items,
  columnId,
  moveItem,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  isRefetching = false,
}: ColumnProps) {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: "ITEM",
      canDrop: () => !isRefetching, // disable dropping while refetching
      drop: (item: { id: number; columnId: TaskStatus }) => {
        if (item.columnId !== columnId) {
          moveItem(item.id, item.columnId, columnId, items.length);
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
    }),
    [isRefetching, columnId, moveItem, items.length],
  );

  const isActive = isOver && canDrop;

  return (
    <div
      key={title}
      ref={drop}
      className={`swim-column ${isActive ? "swim-column--drop-active" : ""}`}
      style={{
        opacity: isRefetching ? 0.6 : 1,
        transition: "opacity 0.2s ease",
      }}
    >
      {items?.length ? (
        <>
          {items.map((item, index) => (
            <KaboodleSwimlaneItem
              key={item.id}
              task={item}
              columnId={columnId}
              index={index}
              moveItem={moveItem}
              isRefetching={isRefetching}
            />
          ))}
          {hasNextPage && (
            <div className="load-more-container">
              <button
                className="load-more-button"
                onClick={fetchNextPage}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <>
                    <LoadingIcon size="small" showText={false} />
                    <span>Loading...</span>
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="no-tasks">
          <div className="no-tasks-header">No Tasks</div>
          <div className="no-tasks-icons">
            <div>
              <span className="material-icons">content_paste</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

KaboodleSwimLaneColumn.displayName = "KaboodleSwimLaneColumn";
